import type { MetricType } from '@repo/api-codegen';
import { makeAutoObservable } from 'mobx';
import type { Monitor, MonitorMetricConfig } from '../../../api';
import type { ScheduleConfig } from '../../../api/types/models/schedule';
import { ScheduleCadence } from '../../../api/types/models/schedule';
import type { PartialWithoutId } from '../../../lib/typescript';
import { supportsGroupedResources } from '../../../pages/MonitorPage/utils';
import { MONITOR_SPECS } from '../monitors';
import type { MonitorSpec } from '../types';
import { isAnalyticsMonitor } from '../types';

export class AddMonitorStore {
	// 1. General
	monitorSpec: MonitorSpec = MONITOR_SPECS[0];

	// 2. Resource section
	integration: string | undefined = undefined;
	integrationError: string = '';

	// - Target based
	targetEntities: string[] = [];
	targetEntitiesError: string = '';

	// - Custom query
	monitorName: string = '';
	querySelect: string = '';

	monitorNameError: string = '';
	querySelectError: string = '';

	// Query where
	queryWhereVisible: boolean = false;
	queryWhere: string = '';

	// Analytics monitor filter
	metricConfig: Record<string, any> = {};
	metricConfigError: string = '';

	// 3. Configure section
	schedule: ScheduleConfig = {
		cadence: ScheduleCadence.DAILY,
		frequency: 1,
		hour: 0,
		day: 0,
	};

	threshold: 'automatic' | 'manual' = 'automatic';
	autoSensitivity: number = 5;
	minSensitivity: number | undefined = undefined;
	maxSensitivity: number | undefined = undefined;

	minSensitivityError: string = '';
	maxSensitivityError: string = '';

	// 4. Manage section
	owners: string[] = [];
	subscribers: string[] = [];
	emailSubscribers: string[] = [];
	tempEmailSubscribers: string[] = [];
	ownersGroups: string[] = [];
	ownersError: string = '';

	// Upstream context for creating monitors on upstream entities
	upstreamOf: string | undefined = undefined;

	constructor(
		public initialMonitorSpec: MonitorSpec,
		public currentUser: string,
		public initialTable?: string,
		public initialIntegration?: string,
		upstreamOf?: string
	) {
		this.monitorSpec = initialMonitorSpec;
		this.owners = [currentUser];
		this.integration = initialIntegration;
		this.initialTable = initialTable;
		this.upstreamOf = upstreamOf;
		makeAutoObservable(this);
	}

	// 1. General
	setTargetEntities = (ids: string[]) => {
		this.targetEntities = ids;
		if (ids.length > 0) {
			this.targetEntitiesError = '';
		}
	};

	// 2. Resource selection
	setIntegration = (integration: string) => {
		this.integration = integration;
		this.integrationError = '';
		this.targetEntities = [];
	};

	// - Custom query
	setMonitorName = (name: string) => {
		this.monitorName = name;
		this.monitorNameError = '';
	};
	setQuerySelect = (query: string) => {
		this.querySelect = query;
		this.querySelectError = '';
	};

	// Query where
	setQueryWhereVisible = (visible: boolean) => {
		this.queryWhereVisible = visible;
	};

	setQueryWhere = (query: string) => {
		this.queryWhere = query;
	};

	// Analytics monitor filter
	setMetricConfigFilter = (key: string, value: string) => {
		this.metricConfig = {
			...this.metricConfig,
			filter: {
				...this.metricConfig.filter,
				[key]: value,
			},
		};
		this.metricConfigError = '';
	};

	clearMetricConfigFilter = (key: string) => {
		if (this.metricConfig.filter) {
			const newFilter = { ...this.metricConfig.filter };
			delete newFilter[key];
			this.metricConfig = {
				...this.metricConfig,
				filter: newFilter,
			};
		}
	};

	// 3. Configure section
	setSchedule = (schedule: ScheduleConfig) => {
		this.schedule = schedule;
	};
	setThreshold = (threshold: 'automatic' | 'manual') => {
		this.threshold = threshold;
	};
	setAutoSensitivity = (sensitivity: number) => {
		this.autoSensitivity = sensitivity;
	};
	setMinSensitivity = (min: number | undefined) => {
		this.minSensitivity = min;
		this.minSensitivityError = '';
		if (
			this.maxSensitivity &&
			this.minSensitivity &&
			this.maxSensitivity > this.minSensitivity
		) {
			this.maxSensitivityError = '';
		} else if (this.maxSensitivity === undefined) {
			this.maxSensitivityError = '';
		}
	};
	setMaxSensitivity = (max: number | undefined) => {
		this.maxSensitivity = max;
		if (
			this.minSensitivity &&
			this.maxSensitivity &&
			this.maxSensitivity > this.minSensitivity
		) {
			this.maxSensitivityError = '';
		} else if (this.minSensitivity === undefined) {
			this.minSensitivityError = '';
			this.maxSensitivityError = '';
		}
	};

	// 4. Manage section
	setOwners = (owners: string[]) => {
		this.owners = owners;
		if (this.owners.length > 0 || this.ownersGroups.length > 0) {
			this.ownersError = '';
		}
	};

	setOwnersGroups = (ownersGroups: string[]) => {
		this.ownersGroups = ownersGroups;
		if (this.owners.length > 0 || this.ownersGroups.length > 0) {
			this.ownersError = '';
		}
	};

	setSubscribers = (subscribers: string[]) => {
		this.subscribers = subscribers;
	};

	setEmailSubscribers = (subs: string[]) => {
		this.emailSubscribers = subs;
	};

	setTempEmailSubscribers = (subs: string[]) => {
		this.tempEmailSubscribers = subs;
	};

	get isFormValid() {
		// 1. General
		// 2. Resource selection
		if (this.integration === undefined) {
			this.integrationError = 'Please select an integration.';
		} else {
			// Data selection only get validated after integration is selected
			if (this.monitorSpec.group === 'Custom') {
				if (this.monitorName.trim() === '') {
					this.monitorNameError =
						"Monitor name can't be empty for custom SQL monitor.";
				}
				if (this.querySelect.trim() === '') {
					this.querySelectError = "SQL query can't be empty.";
				}
			} else if (['Table', 'Column'].includes(this.monitorSpec.group)) {
				if (this.targetEntities.length === 0) {
					this.targetEntitiesError = 'Please select at least one resource.';
				}
			} else if (this.monitorSpec.group === 'Analytics') {
				if (!this.metricConfig.filter?.model_name) {
					this.metricConfigError = 'Please select a dbt model.';
				}
			}
		}
		// 3. Configure section
		if (this.threshold === 'manual') {
			if (
				this.minSensitivity === undefined &&
				this.maxSensitivity === undefined
			) {
				this.minSensitivityError = 'This field is required.';
				this.maxSensitivityError = 'This field is required.';
			}
			if (
				this.minSensitivity !== undefined &&
				this.maxSensitivity !== undefined &&
				this.minSensitivity > this.maxSensitivity
			) {
				this.maxSensitivityError =
					'The maximum value should be greater than the minimum value.';
			}
		}
		// 4. Manage section
		if (this.owners.length === 0 && this.ownersGroups.length === 0) {
			this.ownersError = 'At least one owner is required.';
		}

		return !(
			this.integrationError.length ||
			this.targetEntitiesError.length ||
			this.monitorNameError.length ||
			this.querySelectError.length ||
			this.minSensitivityError.length ||
			this.maxSensitivityError.length ||
			this.ownersError ||
			this.metricConfigError.length
		);
	}

	get monitorsToCreate(): PartialWithoutId<Monitor>[] {
		if (!this.isFormValid) {
			return [];
		}

		const common: PartialWithoutId<Monitor> = {
			owners: this.owners,
			owners_groups: this.ownersGroups,
			subscribers: this.subscribers,
			email_subscribers: this.emailSubscribers,
			schedule: this.schedule,
		};

		if (this.threshold === 'automatic') {
			common['condition_auto_sensitivity'] = this.autoSensitivity;
		} else {
			common['condition_manual_min'] = this.minSensitivity;
			common['condition_manual_max'] = this.maxSensitivity;
		}

		if (this.monitorSpec.group === 'Custom') {
			return [
				{
					integration: this.integration,
					metric_type: this.monitorSpec.type.metric_type as MetricType,
					metric_config: {
						query_select: this.querySelect,
					},
					name: this.monitorName,
					...common,
				},
			];
		}

		if (['Table', 'Column'].includes(this.monitorSpec.group)) {
			const metricConfig: MonitorMetricConfig = {};
			if (this.queryWhere.trim() !== '') {
				metricConfig['query_where'] = this.queryWhere.trim();
			}
			return this.targetEntities.map((id) => ({
				target: id,
				integration: this.integration,
				metric_type: this.monitorSpec.type.metric_type as MetricType,
				metric_config: metricConfig,
				...common,
			}));
		}

		if (
			this.monitorSpec.group === 'Warehouse' &&
			isAnalyticsMonitor(this.monitorSpec.type)
		) {
			const metricConfig: MonitorMetricConfig = {
				integration_metric: this.monitorSpec.type.integration_metric,
			};
			return [
				{
					integration: this.integration,
					metric_type: this.monitorSpec.type.metric_type as MetricType,
					metric_config: metricConfig,
					...common,
				},
			];
		}

		if (
			this.monitorSpec.group === 'Analytics' &&
			isAnalyticsMonitor(this.monitorSpec.type)
		) {
			const metricConfig: MonitorMetricConfig = {
				integration_metric: this.monitorSpec.type.integration_metric,
				filter: this.metricConfig.filter,
			};
			return [
				{
					integration: this.integration,
					metric_type: this.monitorSpec.type.metric_type as MetricType,
					metric_config: metricConfig,
					name: this.monitorName || `dbt ${this.monitorSpec.title}`,
					...common,
				},
			];
		}

		if (['Pipeline'].includes(this.monitorSpec.group)) {
			if (
				supportsGroupedResources(
					this.monitorSpec.type.metric_type as MetricType
				)
			) {
				return [
					{
						integration: this.integration,
						metric_type: this.monitorSpec.type.metric_type as MetricType,
						metric_config: {
							resources: this.targetEntities,
						},
						name: this.monitorName,
						...common,
					},
				];
			}

			return this.targetEntities.map((id) => ({
				target: id,
				integration: this.integration,
				metric_type: this.monitorSpec.type.metric_type as MetricType,
				...common,
			}));
		}

		// TODO[tan]: other monitors
		return [];
	}
}
